import { useRef, useCallback } from 'react';
import * as Cesium from 'cesium';

export class GraticuleImageryProvider {
  readonly tilingScheme: Cesium.TilingScheme;
  readonly tileWidth: number;
  readonly tileHeight: number;
  readonly errorEvent: Cesium.Event;
  readonly hasAlphaChannel: boolean;
  readonly minimumLevel: number;
  readonly maximumLevel: number;
  readonly rectangle: Cesium.Rectangle;
  readonly tileDiscardPolicy: any;
  readonly credit: any;

  constructor() {
    this.tilingScheme = new Cesium.GeographicTilingScheme();
    this.tileWidth = 512;
    this.tileHeight = 512;
    this.errorEvent = new Cesium.Event();
    this.hasAlphaChannel = true;
    this.minimumLevel = 0;
    this.maximumLevel = 20;
    this.rectangle = Cesium.Rectangle.MAX_VALUE;
    this.tileDiscardPolicy = undefined;
    this.credit = undefined;
  }

  get ready() {
    return true;
  }

  get readyPromise() {
    return Promise.resolve(true);
  }

  getTileCredits(x: number, y: number, level: number) {
    return undefined;
  }

  requestImage(x: number, y: number, level: number): Promise<HTMLCanvasElement> {
    const canvas = document.createElement('canvas');
    canvas.width = this.tileWidth;
    canvas.height = this.tileHeight;
    const context = canvas.getContext('2d');
    if (!context) return Promise.resolve(canvas);

    const rect = this.tilingScheme.tileXYToRectangle(x, y, level);
    const west = Cesium.Math.toDegrees(rect.west);
    const south = Cesium.Math.toDegrees(rect.south);
    const east = Cesium.Math.toDegrees(rect.east);
    const north = Cesium.Math.toDegrees(rect.north);

    // Dynamic grid spacing (interval) based on level
    let interval = 30;
    if (level <= 1) interval = 30;
    else if (level === 2) interval = 15;
    else if (level === 3) interval = 10;
    else if (level === 4) interval = 5;
    else if (level === 5) interval = 2;
    else if (level === 6) interval = 1;
    else if (level === 7) interval = 0.5;
    else if (level === 8) interval = 0.2;
    else if (level === 9) interval = 0.1;
    else if (level === 10) interval = 0.05;
    else if (level === 11) interval = 0.02;
    else if (level === 12) interval = 0.01;
    else if (level === 13) interval = 0.005;
    else if (level === 14) interval = 0.002;
    else interval = 0.001;

    // Draw Longitude Lines
    const firstLng = Math.ceil(west / interval) * interval;
    context.strokeStyle = 'rgba(255, 255, 255, 0.2)'; // Sharp thin grid line
    context.lineWidth = 1.0;
    context.fillStyle = 'rgba(255, 255, 255, 0.6)';
    context.font = '11px Inter, Arial, sans-serif';

    for (let lng = firstLng; lng <= east; lng += interval) {
      const pct = (lng - west) / (east - west);
      const canvasX = pct * this.tileWidth;

      context.beginPath();
      context.moveTo(canvasX, 0);
      context.lineTo(canvasX, this.tileHeight);
      context.stroke();

      // Draw label
      let normLng = ((lng + 180) % 360) - 180;
      if (normLng < -180) normLng += 360;
      const val = Number(normLng.toFixed(4));
      const label = val === 0 || Math.abs(val) === 180 ? `${Math.abs(val)}°` : val < 0 ? `${Math.abs(val)}°W` : `${val}°E`;
      
      // Draw label only once per tile, and avoid duplicate labels at the right edge
      if (canvasX >= 0 && canvasX < this.tileWidth - 50) {
        context.fillText(label, canvasX + 4, 18);
      }
    }

    // Draw Latitude Lines
    const firstLat = Math.ceil(south / interval) * interval;
    for (let lat = firstLat; lat <= north; lat += interval) {
      const pct = (north - lat) / (north - south);
      const canvasY = pct * this.tileHeight;

      context.beginPath();
      context.moveTo(0, canvasY);
      context.lineTo(this.tileWidth, canvasY);
      context.stroke();

      // Draw label
      const val = Number(lat.toFixed(4));
      const label = val === 0 ? `0°` : val < 0 ? `${Math.abs(val)}°S` : `${val}°N`;

      // Draw label only once per tile, and avoid duplicate labels at the top edge
      if (canvasY >= 18 && canvasY <= this.tileHeight) {
        context.fillText(label, 8, canvasY - 4);
      }
    }

    return Promise.resolve(canvas);
  }
}

export function useGraticule(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  gridVisibleRef: React.MutableRefObject<boolean>,
) {
  const graticuleLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const secondaryGraticuleLayerRef = useRef<Cesium.ImageryLayer | null>(null);

  const setGridVisible = useCallback((visible: boolean) => {
    gridVisibleRef.current = visible;

    // Primary viewer
    const primaryViewer = viewerRef.current;
    if (primaryViewer) {
      if (visible) {
        if (!graticuleLayerRef.current) {
          const provider = new GraticuleImageryProvider() as any;
          graticuleLayerRef.current = primaryViewer.imageryLayers.addImageryProvider(provider);
        }
      } else {
        if (graticuleLayerRef.current) {
          primaryViewer.imageryLayers.remove(graticuleLayerRef.current);
          graticuleLayerRef.current = null;
        }
      }
    }

    // Secondary viewer
    const secondaryViewer = secondaryViewerRef.current;
    if (secondaryViewer) {
      if (visible) {
        if (!secondaryGraticuleLayerRef.current) {
          const provider = new GraticuleImageryProvider() as any;
          secondaryGraticuleLayerRef.current = secondaryViewer.imageryLayers.addImageryProvider(provider);
        }
      } else {
        if (secondaryGraticuleLayerRef.current) {
          secondaryViewer.imageryLayers.remove(secondaryGraticuleLayerRef.current);
          secondaryGraticuleLayerRef.current = null;
        }
      }
    }
  }, [viewerRef, secondaryViewerRef, gridVisibleRef]);

  return {
    setGridVisible,
  };
}
